import { z } from "zod";

import { hmiBindingSchema } from "./hmi-bindings.schema";
import { hmiExpressionRuleSchema } from "./hmi-expression.schema";
import { hmiSymbolDefinitionSchema, hmiSymbolInstanceSchema } from "./hmi-symbol.schema";

export const HMI_DOCUMENT_KIND = "hmi_canvas";
export const HMI_DOCUMENT_VERSION = 11;

export const hmiElementTypeCatalog = [
  "text",
  "value",
  "stat_card",
  "data_table",
  "rectangle",
  "circle",
  "line",
  "arrow",
  "flow_line",
  "trend_chart",
  "gauge",
  "symbol_instance",
  "svg_asset",
  "image_asset",
  "command_button",
  "command_toggle",
  "setpoint",
] as const;

export const hmiElementTypeSchema = z.enum(hmiElementTypeCatalog);

const elementBaseSchema = z.object({
  id: z.string().trim().min(1).max(128),
  type: hmiElementTypeSchema,
  name: z.string().trim().min(1).max(255),
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
  rotation: z.number().min(-360).max(360).default(0),
  opacity: z.number().min(0).max(1).default(1),
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  zIndex: z.number().int().min(0),
  groupId: z.string().trim().min(1).max(128).nullable().optional(),
  style: z.record(z.string(), z.unknown()).default({}),
  rules: z.array(hmiExpressionRuleSchema).max(16).default([]),
});

const textElementSchema = elementBaseSchema.extend({
  type: z.literal("text"),
  text: z.string().max(4000),
});

const valueElementSchema = elementBaseSchema.extend({
  type: z.literal("value"),
  placeholder: z.string().max(255).default("--"),
});

const statCardElementSchema = elementBaseSchema.extend({
  type: z.literal("stat_card"),
  label: z.string().max(255).default("Stat"),
  placeholder: z.string().max(255).default("--"),
});

const dataTableElementSchema = elementBaseSchema.extend({
  type: z.literal("data_table"),
  table: z.object({
    columns: z.array(z.string().min(1).max(128)).min(1).max(8).default(["Timestamp", "Value"]),
    rowLimit: z.number().int().min(1).max(50).default(8),
  }),
});

const rectangleElementSchema = elementBaseSchema.extend({
  type: z.literal("rectangle"),
  cornerRadius: z.number().min(0).max(999).default(0),
});

const circleElementSchema = elementBaseSchema.extend({
  type: z.literal("circle"),
});

const lineElementSchema = elementBaseSchema.extend({
  type: z.literal("line"),
  points: z.array(z.number()).min(4).max(32),
});

const arrowElementSchema = elementBaseSchema.extend({
  type: z.literal("arrow"),
  points: z.array(z.number()).min(4).max(32),
  arrow: z.object({
    direction: z.enum(["right", "left", "up", "down"]).default("right"),
  }),
});

const flowLineElementSchema = elementBaseSchema.extend({
  type: z.literal("flow_line"),
  flow: z.object({
    shape: z.enum([
      "horizontal",
      "vertical",
      "elbow_right_down",
      "elbow_right_up",
      "elbow_left_down",
      "elbow_left_up",
      "elbow_down_right",
      "elbow_down_left",
      "elbow_up_right",
      "elbow_up_left",
    ]).default("horizontal"),
    direction: z.enum(["forward", "reverse"]).default("forward"),
    animate: z.boolean().default(true),
    speed: z.number().min(4).max(240).default(48),
    stripeLength: z.number().min(4).max(64).default(18),
    stripeGap: z.number().min(2).max(64).default(10),
    stripeColor: z.string().max(64).nullable().optional(),
    lineWidth: z.number().min(4).max(48).default(12),
    turnRatio: z.number().min(0.15).max(0.85).default(0.65),
  }),
});

const trendChartElementSchema = elementBaseSchema.extend({
  type: z.literal("trend_chart"),
  chart: z.object({
    mode: z.enum(["compact", "standard", "area"]).default("compact"),
    showLegend: z.boolean().default(false),
    showAxes: z.boolean().default(true),
    showGrid: z.boolean().default(true),
    lineWidth: z.number().min(1).max(8).default(3),
  }),
});

const gaugeElementSchema = elementBaseSchema.extend({
  type: z.literal("gauge"),
  gauge: z.object({
    min: z.number().default(0),
    warningLow: z.number().default(25),
    warningHigh: z.number().default(75),
    max: z.number().default(100),
    unit: z.string().max(24).nullable().optional(),
    showTicks: z.boolean().default(true),
  }),
});

const symbolInstanceElementSchema = elementBaseSchema.extend({
  type: z.literal("symbol_instance"),
  symbol: hmiSymbolInstanceSchema,
});

const svgAssetElementSchema = elementBaseSchema.extend({
  type: z.literal("svg_asset"),
  asset: z.object({
    assetId: z.string().uuid(),
    preserveAspectRatio: z.string().max(64).default("xMidYMid meet"),
  }),
});

const imageFitSchema = z.enum(["contain", "cover", "fill"]);

const imageAssetElementSchema = elementBaseSchema.extend({
  type: z.literal("image_asset"),
  asset: z.object({
    assetId: z.string().uuid().nullable().optional(),
    fit: imageFitSchema.default("contain"),
    alt: z.string().max(255).nullable().optional(),
  }),
});

const commandStyleVariantSchema = z.enum(["primary", "secondary", "danger"]);
const commandPayloadSchema = z.record(z.string(), z.unknown()).default({});

const commandButtonElementSchema = elementBaseSchema.extend({
  type: z.literal("command_button"),
  command: z.object({
    deviceId: z.string().uuid().nullable().optional(),
    label: z.string().min(1).max(80).default("Send command"),
    action: z.string().min(1).max(80).default("trigger"),
    payload: commandPayloadSchema,
    requireConfirm: z.boolean().default(true),
    confirmText: z.string().max(255).nullable().optional(),
    variant: commandStyleVariantSchema.default("primary"),
  }),
});

const commandToggleElementSchema = elementBaseSchema.extend({
  type: z.literal("command_toggle"),
  toggle: z.object({
    deviceId: z.string().uuid().nullable().optional(),
    label: z.string().min(1).max(80).default("Toggle"),
    action: z.string().min(1).max(80).default("set_output"),
    onLabel: z.string().min(1).max(40).default("On"),
    offLabel: z.string().min(1).max(40).default("Off"),
    onPayload: commandPayloadSchema,
    offPayload: commandPayloadSchema,
    requireConfirm: z.boolean().default(true),
    confirmText: z.string().max(255).nullable().optional(),
  }),
});

const setpointElementSchema = elementBaseSchema.extend({
  type: z.literal("setpoint"),
  setpoint: z.object({
    deviceId: z.string().uuid().nullable().optional(),
    label: z.string().min(1).max(80).default("Setpoint"),
    action: z.string().min(1).max(80).default("set_setpoint"),
    defaultValue: z.number().default(0),
    min: z.number().default(0),
    max: z.number().default(100),
    step: z.number().positive().default(1),
    unit: z.string().max(24).nullable().optional(),
    requireConfirm: z.boolean().default(true),
    confirmText: z.string().max(255).nullable().optional(),
    payload: commandPayloadSchema,
  }),
});

export const hmiElementSchema = z.discriminatedUnion("type", [
  textElementSchema,
  valueElementSchema,
  statCardElementSchema,
  dataTableElementSchema,
  rectangleElementSchema,
  circleElementSchema,
  lineElementSchema,
  arrowElementSchema,
  flowLineElementSchema,
  trendChartElementSchema,
  gaugeElementSchema,
  symbolInstanceElementSchema,
  svgAssetElementSchema,
  imageAssetElementSchema,
  commandButtonElementSchema,
  commandToggleElementSchema,
  setpointElementSchema,
]);

export const hmiCanvasDocumentSchema = z.object({
  kind: z.literal(HMI_DOCUMENT_KIND),
  version: z.literal(HMI_DOCUMENT_VERSION),
  canvas: z.object({
    width: z.number().positive(),
    height: z.number().positive(),
    background: z.object({
      type: z.enum(["solid", "image"]),
      color: z.string().max(64).nullable().optional(),
      assetId: z.string().uuid().nullable().optional(),
    }),
    grid: z.object({
      enabled: z.boolean().default(true),
      size: z.number().positive().default(16),
      snap: z.boolean().default(true),
    }),
  }),
  elements: z.array(hmiElementSchema).max(500).default([]),
  bindings: z.array(hmiBindingSchema).max(2000).default([]),
  symbols: z.array(hmiSymbolDefinitionSchema).max(200).default([]),
  assets: z
    .array(
      z.object({
        id: z.string().uuid(),
        kind: z.enum(["svg", "image"]),
        title: z.string().trim().min(1).max(255),
        source: z.enum(["curated", "imported"]).default("curated"),
        content: z.string().max(40000).nullable().optional(),
        storagePath: z.string().min(1).max(512).nullable().optional(),
        viewBox: z.string().max(128).nullable().optional(),
      }),
    )
    .max(200)
    .default([]),
  variables: z
    .array(
      z.object({
        id: z.string().trim().min(1).max(128),
        name: z.string().trim().min(1).max(128),
        value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
      }),
    )
    .max(200)
    .default([]),
  metadata: z
    .object({
      createdBy: z.string().uuid().nullable().optional(),
      updatedAt: z.string().datetime().nullable().optional(),
      migrationSource: z.string().max(255).nullable().optional(),
      migratedFromVersion: z.number().int().min(1).nullable().optional(),
      workflow: z
        .object({
          publicationStatus: z.enum(["draft", "published"]).default("draft"),
          publishedAt: z.string().datetime().nullable().optional(),
          publishedRevisionId: z.string().trim().min(1).max(128).nullable().optional(),
          templateId: z.string().trim().min(1).max(128).nullable().optional(),
          templateName: z.string().trim().min(1).max(255).nullable().optional(),
        })
        .nullable()
        .optional(),
      revisions: z.array(
        z.object({
          id: z.string().trim().min(1).max(128),
          createdAt: z.string().datetime(),
          note: z.string().max(255).nullable().optional(),
          document: z.unknown(),
        }),
      ).max(20).nullable().optional(),
    })
    .default({}),
});

export type HmiCanvasDocument = z.infer<typeof hmiCanvasDocumentSchema>;
export type HmiElement = z.infer<typeof hmiElementSchema>;
